import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { chargeCarrierConnectService } from '../../services/carrierConnect.service';
import LoaderSpinner from '../common/Loader';
import InputCalculator from '../common/InputCalculator';

/* istanbul ignore next */
const ChargeCarrierConnect = ({ show, handleCancel, carrierConnectId, carrierConnectData, userDetails }) => {
    const [amount, setAmount] = useState('');
    const [errorAmount, setErrorAmount] = useState('');
    const [showLoader, setShowLoader] = useState(false);
    const [comment, setComment] = useState('');
    const [sendEmail, setSendEmail] = useState(true);

    // Validate amount input
    const validateAmount = (value) => {
        if (!value || isNaN(value) || Number(value) <= 0) {
            return 'Please enter a valid amount';
        }
        return '';
    };

    // Handle amount input change with decimal validation
    const handleAmountChange = (e) => {
        const val = e.target.value;

        // Allow empty, numbers, decimal points, and basic math operators
        if (val === "" || /^[\d+\-*/.() ]+$/.test(val)) {
            setAmount(val);
            setErrorAmount("");
        } else {
            setErrorAmount("Invalid characters");
        }
    };

    // Handle input blur for final validation
    const handleBlur = (e) => {
        let { value } = e.target;

        const error = validateAmount(value);

        if (!error && value.endsWith('.')) {
            value = value.slice(0, -1);
            setAmount(value);
        }

        setErrorAmount(error);
    };

    // Handle Charge Carrier Connect button click
    const handleChargeCarrierConnect = async () => {
        const error = validateAmount(amount);
        if (error) {
            setErrorAmount(error);
            return;
        }

        setShowLoader(true);

        // Get carrier connect number from data or generate fallback
        const carrierConnectNumber = carrierConnectData?.carrierConnectNumber || 
                                   `CC-${carrierConnectId}`;
        
        // Get user role from user details
        const userRole = userDetails?.Value?.roleName;

        const payload = {
            carrierConnectId: Number(carrierConnectId),
            carrierConnectNumber: carrierConnectNumber,
            debitAmount: Number(amount),
            debitComment: comment,
            shouldSendEmail: sendEmail,
            userRole: userRole,
            trackingNumber: carrierConnectData?.trackingNumber || '',
            subTrackingNumber: carrierConnectData?.subTrackingNumber || '',
            notes: carrierConnectData?.notes || '',
            packageCount: carrierConnectData?.packageCount || 0,
            createdDate: carrierConnectData?.createdDate || '',
        };

        try {
            const response = await chargeCarrierConnectService(payload);
            
            // Check the actual API response structure
            const statusCode = response?.data?.Status || response?.status;
            const responseData = response?.data;
            
            // Check for success (200 status)
            if (statusCode === 200) {
                toast.success('Carrier Connect charged successfully');
                handleCancel();
            } else if (statusCode === 400) {
                // Handle insufficient funds error or other 400 errors
                const errorMessage = responseData?.Message?.AppStatusDescription || 
                                   responseData?.Message?.AppStatusCode ||
                                   responseData?.message?.appStatusDescription || 
                                   'Insufficient funds in wallet';
                toast.error(errorMessage);
            } else {
                // Handle other errors
                const errorMessage = responseData?.Message?.AppStatusDescription || 
                                   responseData?.Message?.AppStatusCode ||
                                   responseData?.message?.appStatusDescription || 
                                   'Failed to charge carrier connect';
                toast.error(errorMessage);
            }
        } catch (err) {
            // Handle HTTP error responses
            if (err?.response?.status === 400) {
                const responseData = err?.response?.data;
                const errorMessage = responseData?.Message?.AppStatusDescription || 
                                   responseData?.Message?.AppStatusCode ||
                                   responseData?.message?.appStatusDescription || 
                                   'Insufficient funds in wallet';
                toast.error(errorMessage);
            } else {
                const responseData = err?.response?.data;
                const errorMessage = responseData?.Message?.AppStatusDescription || 
                                   responseData?.Message?.AppStatusCode ||
                                   responseData?.message?.appStatusDescription || 
                                   err?.message || 
                                   'An error occurred while charging carrier connect';
                toast.error(errorMessage);
            }
        } finally {
            setShowLoader(false);
        }
    };

    return (
        <Modal show={show} centered backdrop="static" className="modalDeleteWrapper" size="lg" onHide={handleCancel}>
            <Modal.Header closeButton>
                <div className="modal-main-div">
                    <span className="modal-m-text pr-3">Post Charges</span>
                    {showLoader && <LoaderSpinner />}
                </div>
            </Modal.Header>

            <Modal.Body>
                <Row className="flexCenter pt-4 fontFix">
                    <Col sm={12} lg={12} className="formLabel mb-1">
                        Enter Amount ($) <span className="require-field">*</span>
                    </Col>
                    <Col sm={12} lg={12}>
                        <InputCalculator
                            value={amount}
                            onChange={handleAmountChange}
                            onBlur={handleBlur}
                            placeholder="Enter Amount"
                            maxLength={10}
                        />
                    </Col>
                    <Col lg={12}>
                        {errorAmount && <div className="error fontFix">{errorAmount}</div>}
                    </Col>
                    <Col lg={12} className="formLabel mt-3 mb-1">
                        Comment
                    </Col>
                    <Col sm={12} lg={12}>
                        <textarea
                            className="form-control"
                            placeholder="Enter comment"
                            rows={3}
                            maxLength={170}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            onBlur={(e) => setComment(e.target.value.trim())}
                        ></textarea>
                        <div className='mt-2'>
                            <input
                                className="sendNotification mr-1"
                                type="checkbox"
                                checked={sendEmail}
                                onChange={(e) => setSendEmail(e.target.checked)}></input> Send Notification Email
                        </div>
                    </Col>
                </Row>
            </Modal.Body>

            <Row className="modaL_footer mt-1 mr-2">
                <Col className="csv_Buttons">
                    <button
                        className="btn cus-seconday-bg-btn uploadBtn"
                        disabled={showLoader || !!errorAmount || !amount}
                        onClick={handleChargeCarrierConnect}
                    >
                        Charge
                    </button>
                    <button
                        className="btn cus-seconday-bg-btn marginLeft5 cancelBtn"
                        onClick={handleCancel}
                    >
                        Cancel
                    </button>
                </Col>
            </Row>
        </Modal>
    );
};

ChargeCarrierConnect.propTypes = {
    show: PropTypes.bool.isRequired,
    handleCancel: PropTypes.func.isRequired,
    carrierConnectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    carrierConnectData: PropTypes.shape({
        carrierConnectNumber: PropTypes.string,
        trackingNumber: PropTypes.string,
        subTrackingNumber: PropTypes.string,
        notes: PropTypes.string,
        packageCount: PropTypes.number,
        createdDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
    }),
    userDetails: PropTypes.shape({
        Value: PropTypes.shape({
            roleName: PropTypes.string
        })
    })
};

export default ChargeCarrierConnect;
